import { useEffect, useState } from "react";

/**
 * Scroll-spy primitive shared by the in-page navigations (Settings rail,
 * the doc-style `OnThisPageNav`). Returns the id of the section heading nearest
 * the top of the scroll container among `anchors`, so a side nav can light the
 * section currently in view.
 *
 * It auto-locates the nearest scrollable ancestor (the shell's `overflow-auto`
 * content column), falling back to the window, so callers don't have to wire a
 * scroll ref through the tree.
 */

/** Walk up from `el` to the nearest ancestor that actually scrolls. */
export function findScrollParent(el: HTMLElement | null): HTMLElement | null {
  let cur = el?.parentElement ?? null;
  while (cur && cur !== document.body) {
    const style = window.getComputedStyle(cur);
    if (/(auto|scroll)/.test(style.overflowY)) return cur;
    cur = cur.parentElement;
  }
  return null;
}

/**
 * Returns the id of the heading nearest the top of the scroll container among
 * `anchors` (the active section for a TOC). Recomputes on scroll/resize, rAF-
 * throttled. The threshold is 25% down the viewport — the last heading above it
 * wins.
 */
export function useActiveHeading(anchors: string[]): string | null {
  const key = anchors.join("|");
  const [active, setActive] = useState<string | null>(anchors[0] ?? null);

  useEffect(() => {
    if (anchors.length === 0) {
      setActive(null);
      return;
    }
    const els = anchors
      .map((a) => document.getElementById(a))
      .filter((el): el is HTMLElement => el !== null);
    if (els.length === 0) return;

    const root = findScrollParent(els[0] ?? null);
    const scrollEl: HTMLElement | Window = root ?? window;

    let raf = 0;
    function compute() {
      raf = 0;
      const rootTop = root ? root.getBoundingClientRect().top : 0;
      const rootHeight = root ? root.clientHeight : window.innerHeight;
      const threshold = rootTop + rootHeight * 0.25;
      let current: string | null = anchors[0] ?? null;
      for (const el of els) {
        if (el.getBoundingClientRect().top <= threshold) current = el.id;
        else break;
      }
      setActive(current);
    }

    function onScroll() {
      if (raf) return;
      raf = requestAnimationFrame(compute);
    }

    compute();
    scrollEl.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      scrollEl.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  return active;
}
